import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageHeader from "../components/PageHeader";
import WinnerRank, { WinnerName } from "../components/WinnerRank";
import { fixtureStatus, formatKickoff as fmtKickoff } from "../utils/fixtureStatus";
import RulesPayoutsPanel from "../components/RulesPayoutsPanel";
import SmackBoard from "../components/SmackBoard";
import { useAuth } from "../auth/AuthContext";
import { getNflStandings } from "../api/nflSurvivor";
import {
  deletePkSmackMessage,
  getPickemDivisionLeaderboard,
  getPickemLeaderboard,
  getPickemPayouts,
  getPickemPool,
  getPickemWeekResults,
  getPkSmackMessages,
  getPkSmackReplies,
  postPkSmackMessage,
  postPkSmackReply,
  reactPkSmack,
} from "../api/nflPickem";
import { useNflSeason } from "../hooks/useNflSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const DIVIDER_GRAY = "#7a7a7a";
const MAX_WEEK = 18;
// The two frozen leaderboard columns. On a narrow phone they were eating 172px
// of a ~360px viewport, leaving almost nothing visible of PTS and the columns
// after it, so they shrink on mobile and the username truncates rather than
// wrapping to a second line.
const RANK_COL_WIDTH = { base: "34px", md: "52px" };
const USERNAME_COL_WIDTH = { base: "94px", md: "120px" };
// Room for the text inside the username cell, once its padding is taken off.
const USERNAME_TEXT_WIDTH = { base: "82px", md: "104px" };
const FROZEN_COL_PX = { base: 1.5, md: 4 };
// The six scrolling columns carried 16px of padding a side by default — 192px
// of dead space across the row, more than the frozen columns cost.
const DATA_COL_PX = { base: 2, md: 4 };

// Divider under a division heading. Darker than the default row rule so
// the eight divisions read as distinct blocks rather than one long list.
const DIVISION_RULE = "gray.400";

/** "3 times", "1 time" — the rules read as prose, not as a settings dump. */
function plural(n, word) {
  return `${n} ${word}${Number(n) === 1 ? "" : "s"}`;
}

/**
 * The rules for one pool.
 *
 * Four of these lines are admin settings, so this is built from the pool
 * rather than held as a constant — a pool that raised its pick-against limit
 * or turned the Underdog Bonus off was still being shown the defaults.
 */
function pickemRules(pool) {
  const againstLimit = pool?.pick_against_limit ?? 3;
  const lockWeek = pool?.division_lock_week ?? 4;
  const underdogOn = !!pool?.underdog_bonus_enabled;
  const threshold = Number(pool?.underdog_threshold ?? 4.5);
  const uses = Number(pool?.underdog_uses ?? 1);

  return [
    {
      heading: "Picking",
      lines: [
        "Pick one team each week and assign it 1–5 confidence points.",
        "Picks lock at that game's kickoff.",
        "A team may only be picked to win once all season.",
        `You may pick against the same team ${plural(againstLimit, "time")} all season.`,
        "Miss a pick and you get the worst-odds team at 5 confidence points.",
      ],
    },
    {
      heading: "Scoring",
      lines: [
        "Win: +confidence points.",
        "Loss: −confidence points.",
        "Tie: 0 points.",
        "Boat Race Bonus: +2 for winning by 21 or more.",
        // Zero uses is the same thing as off, however the switch is set.
        underdogOn && uses > 0
          ? `Underdog Bonus: doubles the confidence points you stake on a team getting ${threshold} or more points — double if it wins, double if it loses. ${plural(uses, "use")} per season.`
          : "Underdog Bonus: not used in this pool.",
      ],
    },
    {
      heading: "Division Predictions",
      lines: [
        "Predict the finishing order of all eight divisions.",
        `Locks when Week ${lockWeek} kicks off.`,
        "Scored on how many places out you are — lowest total wins.",
        "Ranked on its own leaderboard.",
      ],
    },
    {
      heading: "Winning",
      lines: [
        "Most points wins.",
        "Ties broken by total wins, then combined margin.",
      ],
    },
  ];
}


/** Points can be negative, so colour carries the meaning at a glance. */
function pointsColor(points) {
  if (points == null) return "gray.500";
  if (points > 0) return "green.600";
  if (points < 0) return "red.600";
  return "gray.600";
}

export default function NflPickemActiveHome() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { season, seasonId, loading: seasonLoading } = useNflSeason();
  const effectiveSeasonId = location.state?.seasonId ?? seasonId;
  const numericPoolId = Number(poolId);

  const [pool, setPool] = useState(null);
  const [board, setBoard] = useState(null);
  const [divBoard, setDivBoard] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [standings, setStandings] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const {
    isOpen: isExpandedStatsOpen,
    onOpen: onExpandedStatsOpen,
    onClose: onExpandedStatsClose,
  } = useDisclosure();
  const [scheduleWeek, setScheduleWeek] = useState(null);
  const [loadingWeek, setLoadingWeek] = useState(false);

  // The schedule sizes to its 16 fixtures and the leaderboard is matched to
  // it. Stretching instead would let whichever box is taller win, and with a
  // large pool that is the leaderboard — leaving dead space under the games.
  const scheduleRef = useRef(null);
  const [scheduleHeight, setScheduleHeight] = useState(null);

  const isDesktop = useBreakpointValue({ base: false, md: true }) ?? false;

  // Week 1 until the season starts, then whichever week is under way. The
  // schedule box can then be paged away from it without refetching the page.
  const currentWeek = season?.current_week || 1;
  const shownWeek = scheduleWeek ?? currentWeek;

  const load = useCallback(async () => {
    if (!effectiveSeasonId || !poolId) return;
    setLoading(true);
    setError("");
    try {
      const [p, lb, dlb, pay, st] = await Promise.all([
        getPickemPool(effectiveSeasonId, poolId),
        getPickemLeaderboard(effectiveSeasonId, poolId),
        getPickemDivisionLeaderboard(effectiveSeasonId, poolId).catch(() => ({ rows: [] })),
        getPickemPayouts(effectiveSeasonId, poolId).catch(() => []),
        getNflStandings(effectiveSeasonId).catch(() => null),
      ]);
      setPool(p);
      setBoard(lb);
      setDivBoard(dlb);
      setPayouts(pay);
      setStandings(st);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load this pool.");
    } finally {
      setLoading(false);
    }
  }, [effectiveSeasonId, poolId]);

  useEffect(() => { if (!seasonLoading) load(); }, [load, seasonLoading]);

  // Paging the schedule refetches only that box.
  const loadWeek = useCallback(async () => {
    if (!effectiveSeasonId || !poolId) return;
    setLoadingWeek(true);
    try {
      setResults(await getPickemWeekResults(effectiveSeasonId, poolId, shownWeek));
    } catch {
      setResults(null);
    } finally {
      setLoadingWeek(false);
    }
  }, [effectiveSeasonId, poolId, shownWeek]);

  useEffect(() => { if (!seasonLoading) loadWeek(); }, [loadWeek, seasonLoading]);

  useEffect(() => {
    const el = scheduleRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => setScheduleHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [results, loadingWeek, loading]);

  const rows = board?.rows || [];
  const poolFinished = board?.pool_finished === true;
  const rules = useMemo(() => pickemRules(pool), [pool]);
  const isAdmin =
    pool?.owner_user_id === user?.id || pool?.current_user_is_co_admin;

  // Flatten the grouped standings for the compact division tables.
  const divisions = useMemo(() => {
    const out = [];
    (standings?.conferences || []).forEach((c) =>
      (c.divisions || []).forEach((d) =>
        out.push({ key: `${c.conference} ${d.division}`, teams: d.teams || [] })
      )
    );
    return out;
  }, [standings]);

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loading) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <PageHeader title="NFL PICK 'EM" onBack={() => navigate("/nfl-pickem")}
                    user={user} onLogout={handleLogout} />
        <Flex justify="center" align="center" minH="300px"><Spinner size="xl" /></Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader title="NFL PICK 'EM" onBack={() => navigate("/nfl-pickem")}
                    user={user} onLogout={handleLogout} />

      <Container maxW="container.xl" py={{ base: 5, md: 8 }}>
        {error && (
          <Alert status="error" borderRadius="md" mb={6} color="red.900">
            <AlertIcon />{error}
          </Alert>
        )}

        <SmackBoard
          poolId={numericPoolId}
          seasonId={effectiveSeasonId}
          apiFns={{
            getMessages: getPkSmackMessages,
            postMessage: postPkSmackMessage,
            getReplies: getPkSmackReplies,
            postReply: postPkSmackReply,
            react: reactPkSmack,
            deleteMessage: deletePkSmackMessage,
          }}
        />

        <HStack spacing={3} mb={4} mt={4} flexWrap="wrap">
          <Button
            bg="red.600"
            color="white"
            _hover={{ bg: "red.700" }}
            onClick={() => navigate(`/nfl-pickem/picks/${numericPoolId}`,
                                    { state: { seasonId: effectiveSeasonId } })}
          >
            Make/Review Picks
          </Button>
          <Box flex="1" display={{ base: "none", md: "block" }} />
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              color="white"
              borderColor="whiteAlpha.500"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => navigate(`/nfl-pickem/setup/${numericPoolId}`,
                                      { state: { seasonId: effectiveSeasonId } })}
            >
              Pool Settings
            </Button>
          )}
        </HStack>

        {/* ── Leaderboard + this week's schedule ── */}
        <HStack align="flex-start" spacing={6} flexWrap="wrap">
          <Box flex="2" minW={{ base: "100%", md: "480px" }} bg="white" color="black"
               border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg"
               p={{ base: 4, md: 6 }} display="flex" flexDirection="column"
               h={{ base: "520px", md: scheduleHeight ? `${scheduleHeight}px` : "auto" }}>
            <Flex justify="space-between" align="center" mb={4} gap={3}>
              <Heading size="md" color={TOP_BAR_BLUE}>
                {pool?.name ? `${pool.name} Leaderboard` : "Leaderboard"}
              </Heading>
              <Button
                size="xs"
                bg={TOP_BAR_BLUE}
                color="white"
                _hover={{ bg: "#000080" }}
                flexShrink={0}
                onClick={() => navigate(`/nfl-pickem/all-picks/${poolId}`,
                                        { state: { seasonId: effectiveSeasonId } })}
              >
                All Picks
              </Button>
            </Flex>

            {rows.length === 0 ? (
              <Text color="gray.600">No members yet.</Text>
            ) : (
              <Box overflowX="auto" overflowY="auto" flex="1" minH={0} maxH="100%">
                <Table size="sm" variant="simple"
                       sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                  <Thead>
                    <Tr>
                      <Th position="sticky" top={0} left={0} zIndex={4} bg="white"
                          w={RANK_COL_WIDTH} minW={RANK_COL_WIDTH} px={FROZEN_COL_PX}
                          borderBottom="2px solid" borderColor="gray.200">
                        {/* "Rank" spelled out would set the column wider than
                            the numbers underneath it need on a phone. */}
                        {isDesktop ? "Rank" : "#"}
                      </Th>
                      <Th position="sticky" top={0} left={RANK_COL_WIDTH} zIndex={4} bg="white"
                          w={USERNAME_COL_WIDTH} minW={USERNAME_COL_WIDTH} px={FROZEN_COL_PX}
                          borderBottom="2px solid" borderColor="gray.200">
                        {isDesktop ? "Username" : "User"}
                      </Th>
                      {["PTS", "Wins", "PD", "Win Streak", "Last Pick", "Last Pts"].map((label) => (
                        <Th key={label} isNumeric={label !== "Last Pick"}
                            position="sticky" top={0} zIndex={3} bg="white"
                            px={DATA_COL_PX} whiteSpace="nowrap"
                            borderBottom="2px solid" borderColor="gray.200">
                          {label}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => (
                      <Tr key={r.user_id}>
                        <Td position="sticky" left={0} zIndex={2} bg="white"
                            w={RANK_COL_WIDTH} minW={RANK_COL_WIDTH} px={FROZEN_COL_PX}>
                          <WinnerRank rank={r.rank} finished={poolFinished} placeholder="—" />
                        </Td>
                        <Td position="sticky" left={RANK_COL_WIDTH} zIndex={2} bg="white"
                            w={USERNAME_COL_WIDTH} minW={USERNAME_COL_WIDTH}
                            px={FROZEN_COL_PX} fontWeight="medium">
                          {/* A td's width is only a hint under auto layout, so
                              the clamp lives on an inner box where maxW and
                              text-overflow are actually honoured. */}
                          <Box maxW={USERNAME_TEXT_WIDTH} overflow="hidden"
                               whiteSpace="nowrap" textOverflow="ellipsis"
                               title={r.username}>
                            {r.username}
                          </Box>
                        </Td>
                        <Td isNumeric px={DATA_COL_PX} fontWeight="bold"
                            color={pointsColor(r.points)}>
                          {r.points}
                        </Td>
                        <Td isNumeric px={DATA_COL_PX}>{r.wins}</Td>
                        <Td isNumeric px={DATA_COL_PX} color={pointsColor(r.margin)}>
                          {r.margin > 0 ? "+" : ""}{r.margin}
                        </Td>
                        <Td isNumeric px={DATA_COL_PX}>{r.win_streak || "—"}</Td>
                        <Td px={DATA_COL_PX}>
                          {r.last_pick ? (
                            <HStack spacing={1}>
                              {r.last_pick.logo_url && (
                                <Image src={r.last_pick.logo_url} boxSize="16px"
                                       objectFit="contain" />
                              )}
                              <Text fontSize="xs">{r.last_pick.short_name}</Text>
                            </HStack>
                          ) : (
                            <Text fontSize="xs" color="gray.400">—</Text>
                          )}
                        </Td>
                        <Td isNumeric px={DATA_COL_PX} color={pointsColor(r.last_points)}>
                          {r.last_points ?? "—"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>

          {/* Week schedule */}
          <Box ref={scheduleRef} flex="1" minW={{ base: "100%", md: "300px" }}
               bg="white" color="black"
               border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg"
               p={{ base: 4, md: 6 }} display="flex" flexDirection="column">
            {/* Title and week stepper share a row on desktop; the stepper drops
                beneath on mobile, where the box is full width but narrow. */}
            <Flex align="center" justify="space-between" gap={2} mb={3}
                  direction={{ base: "column", md: "row" }}>
              <Heading size="md" color={TOP_BAR_BLUE} whiteSpace="nowrap">
                Week Fixtures/Results
              </Heading>
              <HStack spacing={1} flexShrink={0}>
                <Button size="xs" onClick={() => setScheduleWeek(shownWeek - 1)}
                        isDisabled={shownWeek <= 1 || loadingWeek}>
                  ◀
                </Button>
                <Text fontWeight="bold" color={TOP_BAR_BLUE} minW="40px" textAlign="center">
                  WK {shownWeek}
                </Text>
                <Button size="xs" onClick={() => setScheduleWeek(shownWeek + 1)}
                        isDisabled={shownWeek >= MAX_WEEK || loadingWeek}>
                  ▶
                </Button>
              </HStack>
            </Flex>
            {loadingWeek ? (
              <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
            ) : !results?.games?.length ? (
              <Text color="gray.600" fontSize="sm">No games scheduled.</Text>
            ) : (
              <Box flex="1">
                <VStack align="stretch" spacing={2}>
                  {results.games.map((g) => (
                    <Box key={g.id} border="1px solid #d9d9d9" borderRadius="md"
                         px={3} py={2}>
                      <HStack spacing={2} justify="space-between">
                        {/* Score replaces the "@" once final, mirroring the PL
                            box where it replaces "vs". Away team reads first. */}
                        <HStack spacing={1} minW="0">
                          {g.away_team?.logo_url && (
                            <Image src={g.away_team.logo_url} boxSize="18px"
                                   objectFit="contain" flexShrink={0} />
                          )}
                          <Text fontSize="xs" whiteSpace="nowrap">{g.away_team?.short_name}</Text>
                          {g.is_final && g.away_score != null && g.home_score != null ? (
                            <Text fontSize="xs" fontWeight="bold" whiteSpace="nowrap" px={1}>
                              {g.away_score} - {g.home_score}
                            </Text>
                          ) : (
                            <Text fontSize="0.6rem" color="gray.400">@</Text>
                          )}
                          {g.home_team?.logo_url && (
                            <Image src={g.home_team.logo_url} boxSize="18px"
                                   objectFit="contain" flexShrink={0} />
                          )}
                          <Text fontSize="xs" whiteSpace="nowrap">{g.home_team?.short_name}</Text>
                        </HStack>
                        {(() => {
                          const st = fixtureStatus(g.kickoff_utc, g.is_final);
                          return (
                            <Text fontSize="xs" fontWeight="semibold"
                                  whiteSpace="nowrap" color={st.color}>
                              {st.label}
                            </Text>
                          );
                        })()}
                      </HStack>
                      {fmtKickoff(g.kickoff_utc) && (
                        <Text fontSize="0.65rem" color="gray.500" mt={0.5}>
                          {fmtKickoff(g.kickoff_utc)}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </Box>
        </HStack>

        {/* ── Division standings prediction ── */}
        <Box mt={8} bg={TOP_BAR_BLUE} borderTop={`2px solid ${DIVIDER_GRAY}`}
             borderBottom={`2px solid ${DIVIDER_GRAY}`} boxShadow="md"
             px={6} py={4} borderRadius="md">
          <Heading fontFamily="'Orbitron', sans-serif"
                   fontSize={{ base: "20px", md: "28px" }}
                   letterSpacing="2px" fontWeight="600">
            DIVISION STANDINGS PREDICTION
          </Heading>
        </Box>

        <HStack align="start" spacing={6} flexWrap="wrap" mt={4} mb={4}>
          <Button bg="red.600" color="white" _hover={{ bg: "red.700" }}
                  onClick={() => navigate(`/nfl-pickem/divisions/${numericPoolId}`,
                                          { state: { seasonId: effectiveSeasonId } })}>
            Make / Review Prediction
          </Button>
        </HStack>

        <HStack align="stretch" spacing={6} flexWrap="wrap" mt={2}>
          {/* Current standings */}
          <Box flex="1" minW={{ base: "100%", md: "280px" }} bg="white" color="black"
               border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg"
               p={{ base: 4, md: 6 }}>
            <Flex align="center" justify="space-between" mb={4} gap={3} flexWrap="wrap">
              <Heading size="md" color={TOP_BAR_BLUE}>Current Standings</Heading>
              <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE}
                      color={TOP_BAR_BLUE} _hover={{ bg: "gray.100" }}
                      onClick={onExpandedStatsOpen}>
                Expanded Stats
              </Button>
            </Flex>

            {divisions.length === 0 ? (
              <Text color="gray.600" fontSize="sm">
                Standings appear once the season starts.
              </Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Division</Th>
                      <Th isNumeric>W</Th>
                      <Th isNumeric>L</Th>
                      <Th isNumeric>T</Th>
                      <Th isNumeric>PD</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {divisions.map((d) => (
                      <React.Fragment key={d.key}>
                        <Tr>
                          <Td colSpan={5} fontWeight="bold" bg="gray.50"
                              fontSize="xs" color={TOP_BAR_BLUE}
                              borderBottom="2px solid" borderColor={DIVISION_RULE}>
                            {d.key}
                          </Td>
                        </Tr>
                        {d.teams.map((row) => (
                          <Tr key={row.team?.id}>
                            <Td>
                              <HStack spacing={2}>
                                <Text fontSize="0.6rem" color="gray.500" w="14px">
                                  {row.position ?? "—"}
                                </Text>
                                {row.team?.logo_url && (
                                  <Image src={row.team.logo_url} boxSize="16px"
                                         objectFit="contain" />
                                )}
                                <Text fontSize="xs" whiteSpace="nowrap">
                                  {isDesktop ? row.team?.name : row.team?.short_name}
                                </Text>
                              </HStack>
                            </Td>
                            <Td isNumeric fontSize="xs">{row.wins}</Td>
                            <Td isNumeric fontSize="xs">{row.losses}</Td>
                            <Td isNumeric fontSize="xs">{row.ties}</Td>
                            <Td isNumeric fontSize="xs" color={pointsColor(row.point_diff)}>
                              {row.point_diff > 0 ? "+" : ""}{row.point_diff}
                            </Td>
                          </Tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>

          {/* Prediction leaderboard */}
          <Box flex="2" minW={{ base: "100%", md: "480px" }} bg="white" color="black"
               border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg"
               p={{ base: 4, md: 6 }}
               display="flex" flexDirection="column">
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}
                  mb={2} minH="40px">
              <Heading size="md" color={TOP_BAR_BLUE} lineHeight="1">
                Division Predictions - Sorted by Score
              </Heading>
            </Flex>

            {!(divBoard?.rows || []).length ? (
              <VStack align="center" justify="center" py={8} spacing={2} flex="1">
                <Text color="gray.500" textAlign="center">
                  Predictions are open until week {pool?.division_lock_week ?? 4} kicks off.
                </Text>
                <Text color="gray.400" fontSize="sm" textAlign="center">
                  Submit your predicted final division standings before it locks!
                </Text>
              </VStack>
            ) : (
              <Box overflowX="auto" overflowY="auto" flex="1" minH={0}>
                {/* One column per player, mirroring the PL table predictor.
                    The score sits beside each name rather than in its own
                    row, keeping the header one line deep so the division rows
                    line up with it. */}
                <Table size="sm" variant="simple"
                       sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                  <Thead>
                    <Tr>
                      <Th position="sticky" top={0} left={0} zIndex={4} bg="white"
                          minW="120px"
                          borderBottom="2px solid" borderColor="gray.200">
                        Division
                      </Th>
                      {divBoard.rows.map((r, ci) => (
                        <Th key={r.user_id} position="sticky" top={0} zIndex={3}
                            minW="88px" whiteSpace="nowrap"
                            sx={{ textAlign: "left !important" }}
                            borderBottom="2px solid" borderColor="gray.200"
                            bg={r.user_id === user?.id
                                ? "yellow.100"
                                : ci % 2 === 0 ? "white" : "gray.50"}
                            fontWeight={r.user_id === user?.id ? "bold" : "normal"}>
                          <HStack spacing={1} align="baseline">
                            {/* Lowest score wins and the rank is dense, so
                                players who finish level all hold rank 1 and
                                all get a trophy. */}
                            <WinnerName name={r.username} fontSize="xs" noOfLines={1}
                                        size={11}
                                        isWinner={poolFinished && r.rank === 1} />
                            <Text fontSize="xs" fontWeight="bold"
                                  color={r.score == null && divBoard.locked
                                         ? "gray.500" : TOP_BAR_BLUE}>
                              {/* A null score means not every division could be
                                  scored. Before the lock that is progress, so
                                  show the fraction; afterwards it can never be
                                  completed, so DNF says so in less space. */}
                              {r.score ?? (divBoard.locked
                                ? "DNF"
                                : `${r.divisions_entered}/8`)}
                            </Text>
                          </HStack>
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(divBoard.divisions || []).map((divName) => (
                      <React.Fragment key={divName}>
                        <Tr>
                          <Td colSpan={(divBoard.rows?.length || 0) + 1} bg="gray.50"
                              fontWeight="bold" fontSize="0.65rem" color={TOP_BAR_BLUE}
                              borderBottom="2px solid" borderColor={DIVISION_RULE}>
                            {divName}
                          </Td>
                        </Tr>
                        {[0, 1, 2, 3].map((slot) => (
                          <Tr key={`${divName}-${slot}`}>
                            <Td position="sticky" left={0} zIndex={2} bg="white"
                                fontSize="xs" color={TOP_BAR_BLUE}>
                              {slot + 1}
                            </Td>
                            {divBoard.rows.map((r, ci) => {
                              const team = (r.picks?.[divName] || [])[slot];
                              return (
                                <Td key={r.user_id} fontSize="xs" whiteSpace="nowrap"
                                    bg={r.user_id === user?.id
                                        ? "yellow.50"
                                        : ci % 2 === 0 ? "white" : "gray.50"}
                                    fontWeight={r.user_id === user?.id ? "bold" : "normal"}>
                                  {team?.short_name || "—"}
                                </Td>
                              );
                            })}
                          </Tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </HStack>
      </Container>

      <RulesPayoutsPanel
        rules={rules}
        payouts={payouts}
        totalPot={pool?.total_pot}
      />

      {/* Expanded standings — a modal rather than extra inline columns, matching
          the Premier League page. */}
      <Modal isOpen={isExpandedStatsOpen} onClose={onExpandedStatsClose}
             isCentered size="6xl" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" color="black"
                      border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" maxW="95vw">
          <ModalHeader color={TOP_BAR_BLUE}>Expanded Standings</ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />
          <ModalBody pb={6}>
            {divisions.length === 0 ? (
              <Text color="gray.600" fontSize="sm">
                Standings appear once the season starts.
              </Text>
            ) : (
              // 32 teams plus 8 division rows is far longer than the PL's 20,
              // so this scrolls rather than running off the viewport.
              <Box overflowX="auto" overflowY="auto"
                   maxH={{ base: "65vh", md: "70vh" }}
                   border="1px solid" borderColor="gray.200"
                   borderRadius="md" boxShadow="sm">
                {/* separate borders so the sticky header paints over the rows
                    beneath it instead of showing through. */}
                <Table size="sm" variant="simple"
                       sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                  <Thead>
                    <Tr>
                      {[
                        { label: "Team", numeric: false },
                        { label: "W", numeric: true },
                        { label: "L", numeric: true },
                        { label: "T", numeric: true },
                        { label: "PF", numeric: true },
                        { label: "PA", numeric: true },
                        { label: "PD", numeric: true },
                        { label: "Home", numeric: false },
                        { label: "Road", numeric: false },
                        { label: "Conf", numeric: false },
                        { label: "Div", numeric: false },
                        { label: "Streak", numeric: false },
                      ].map(({ label, numeric }) => (
                        <Th key={label} isNumeric={numeric}
                            bg={TOP_BAR_BLUE} color="white"
                            position="sticky" top={0} zIndex={2}>
                          {label}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {divisions.map((d) => (
                      <React.Fragment key={`exp-${d.key}`}>
                        <Tr>
                          <Td colSpan={12} fontWeight="bold" fontSize="xs"
                              bg="gray.100" color={TOP_BAR_BLUE}
                              borderBottom="2px solid" borderColor={DIVISION_RULE}>
                            {d.key}
                          </Td>
                        </Tr>
                        {d.teams.map((row) => (
                          <Tr key={`exp-${row.team?.id}`}>
                            <Td>
                              <HStack spacing={2}>
                                <Text fontSize="0.6rem" color="gray.500" w="14px">
                                  {row.position ?? "—"}
                                </Text>
                                {row.team?.logo_url && (
                                  <Image src={row.team.logo_url} boxSize="16px"
                                         objectFit="contain" />
                                )}
                                <Text fontSize="xs" whiteSpace="nowrap">
                                  {row.team?.name}
                                </Text>
                              </HStack>
                            </Td>
                            <Td isNumeric fontSize="xs">{row.wins}</Td>
                            <Td isNumeric fontSize="xs">{row.losses}</Td>
                            <Td isNumeric fontSize="xs">{row.ties}</Td>
                            <Td isNumeric fontSize="xs">{row.points_for}</Td>
                            <Td isNumeric fontSize="xs">{row.points_against}</Td>
                            <Td isNumeric fontSize="xs" color={pointsColor(row.point_diff)}>
                              {row.point_diff > 0 ? "+" : ""}{row.point_diff}
                            </Td>
                            <Td fontSize="xs">{row.record_home || "—"}</Td>
                            <Td fontSize="xs">{row.record_road || "—"}</Td>
                            <Td fontSize="xs">{row.record_conference || "—"}</Td>
                            <Td fontSize="xs">{row.record_division || "—"}</Td>
                            <Td fontSize="xs">{row.streak || "—"}</Td>
                          </Tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
